import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Container, Nav, Row, Tab } from 'react-bootstrap';
import {
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaExclamationTriangle,
  FaHourglassHalf,
  FaSitemap,
  FaTools,
  FaUserCog,
  FaUserTie,
  FaUsers
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import AuditProgress from '../../Components/AuditProgress';

function IndividualDashboard() {
  const [stats, setStats] = useState({
    branchesCount: 0,
    departmentsCount: 0,
    departmentUsersCount: 0,
    auditorsCount: 0,
    checklistsCount: 0,
  });
  const [checklistStatus, setChecklistStatus] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0
  });
  const [capStatus, setCapStatus] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        setAdmin(userData);

        if (!userData || !userData.BranchCode) {
          throw new Error('Admin data not found');
        }

        if (userData.id) {
          try {
            const profileResponse = await fetch(`${BASE_URL}/get-user-data/${userData.id}`);
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              if (profileData.profileImage) {
                setProfileImage(`${BASE_URL}${profileData.profileImage}`);
              }
            }
          } catch (profileError) {
            console.error("Failed to load profile image:", profileError);
          }
        }

        const response = await fetch(`${BASE_URL}/get-admin-dashboard-stats/${userData.BranchCode}`);
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard statistics');
        }

        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
          setChecklistStatus(data.checklistStatus);
        } else {
          throw new Error(data.error || 'Failed to fetch dashboard statistics');
        }

        const capResponse = await fetch(`${BASE_URL}/get-cap-dashboard-stats/${userData.BranchCode}`);
        if (capResponse.ok) {
          const capData = await capResponse.json();
          if (capData.success) {
            setCapStatus(capData.capStatus);
          }
        }

      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [BASE_URL]);

  const getAdminInitials = () => {
    if (!admin) return "AD";
    return `${admin.firstName?.charAt(0) || ''}${admin.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <Container fluid className="dashboard-container p-0">
      {/* Integrated header content without the styled header section */}
      <Container fluid className="px-4 pt-4 pb-2">
        <Row className="align-items-center mb-3">
          <Col md={6} className="mb-3 mb-md-0">
            <div className="d-flex align-items-center">
              {profileImage ? (
                <div className="admin-avatar-simple me-3">
                  <img 
                    src={profileImage} 
                    alt={`${admin?.firstName} ${admin?.lastName}`}
                    className="profile-image"
                  />
                </div>
              ) : (
                <div className="admin-avatar-simple me-3">
                  {getAdminInitials()}
                </div>
              )}
              <div>
                <h4 className="greeting mb-0 text-primary">Welcome {admin?.firstName || 'Admin'} {admin?.lastName || ''}</h4>
                <p className="mb-0 text-muted">
                  <FaCalendarAlt className="me-1" size={14} /> {currentDate}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      
      <Container fluid className="dashboard-content px-4 pb-3">
        {error && (
          <Alert variant="danger" className="mb-3">
            <Alert.Heading>Error Loading Dashboard</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Dashboard</p>
            <p className="text-muted">Please wait while we fetch your stats...</p>
          </div>
        ) : (
          <div className="dashboard-grid">
            <div className="dashboard-section stats-overview">
              <h5 className="section-title mb-3">
                <FaChartLine className="me-2" /> Organization Overview
              </h5>
              <div className="stats-cards">
                <Link to="/IndividualBranch" className="text-decoration-none">
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon branch-icon">
                        <FaBuilding />
                      </div>
                      <div className="stat-content">
                        <h4 className="stat-value">{stats.branchesCount}</h4>
                        <p className="stat-label">Branches</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
                <Link to="/IndividualDepartment" className="text-decoration-none">
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon department-icon">
                        <FaSitemap />
                      </div>
                      <div className="stat-content">
                        <h4 className="stat-value">{stats.departmentsCount}</h4>
                        <p className="stat-label">Departments</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
                <Link to="/IndividualDepartmentUser" className="text-decoration-none">
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon users-icon">
                        <FaUsers />
                      </div>
                      <div className="stat-content">
                        <h4 className="stat-value">{stats.departmentUsersCount}</h4>
                        <p className="stat-label">Department Users</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
                <Link to="/IndividualAuditorCreate" className="text-decoration-none">
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon auditor-icon">
                        <FaUserTie />
                      </div>
                      <div className="stat-content">
                        <h4 className="stat-value">{stats.auditorsCount}</h4>
                        <p className="stat-label">Auditors</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
                <Link to="/ChecklistOverview" className="text-decoration-none">
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon checklist-icon">
                        <FaClipboardList />
                      </div>
                      <div className="stat-content">
                        <h4 className="stat-value">{stats.checklistsCount}</h4>
                        <p className="stat-label">Checklists</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </div>
            </div>

            <div className="dashboard-section status-section">
              <Tab.Container id="status-tabs" defaultActiveKey="audits">
                <Nav variant="tabs" className="status-tabs mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="audits" className="status-tab">Audit Status</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="caps" className="status-tab">Corrective Action Plans</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="audits">
                    <div className="status-cards">
                      <Link to="/IndividualAuditOverview?status=pending" className="text-decoration-none">
                        <Card className="status-card">
                          <Card.Body>
                            <div className="status-icon pending-icon">
                              <FaClock />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{checklistStatus.pending}</h4>
                              <p className="status-label">Plan</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                      <Link to="/IndividualAuditOverview?status=inProgress" className="text-decoration-none">
                        <Card className="status-card">
                          <Card.Body>
                            <div className="status-icon progress-icon">
                              <FaHourglassHalf />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{checklistStatus.inProgress}</h4>
                              <p className="status-label">In Progress</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                      <Link to="/IndividualAuditOverview?status=completed" className="text-decoration-none">
                        <Card className="status-card">
                          <Card.Body>
                            <div className="status-icon complete-icon">
                              <FaCheckCircle />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{checklistStatus.completed}</h4>
                              <p className="status-label">Completed</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="caps">
                    <div className="status-cards">
                      <Link to="/IndividualReAuditOverview?status=pending" className="text-decoration-none">
                        <Card className="status-card">
                          <Card.Body>
                            <div className="status-icon cap-pending-icon">
                              <FaExclamationTriangle />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{capStatus.pending}</h4>
                              <p className="status-label">Plan</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                      <Link to="/IndividualReAuditOverview?status=inProgress" className="text-decoration-none">
                        <Card className="status-card">
                          <Card.Body>
                            <div className="status-icon cap-progress-icon">
                              <FaTools />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{capStatus.inProgress}</h4>
                              <p className="status-label">In Progress</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                      <Link to="/IndividualReAuditOverview?status=completed" className="text-decoration-none">
                        <Card className="status-card">
                          <Card.Body>
                            <div className="status-icon cap-complete-icon">
                              <FaCheckCircle />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{capStatus.completed}</h4>
                              <p className="status-label">Completed</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>

            <div className="dashboard-section progress-section">
              <AuditProgress />
            </div>

            <div className="dashboard-section actions-section">
              <h5 className="section-title mb-3">
                <FaUserCog className="me-2" /> Quick Actions
              </h5>
              <div className="quick-actions">
                <Link to="/IndividualBranch" className="action-card">
                  <div className="action-icon branch-action">
                    <FaBuilding />
                  </div>
                  <span>Add Branch</span>
                </Link>
                <Link to="/IndividualDepartment" className="action-card">
                  <div className="action-icon department-action">
                    <FaSitemap />
                  </div>
                  <span>Add Department</span>
                </Link>
                <Link to="/IndividualDepartmentUser" className="action-card">
                  <div className="action-icon users-action">
                    <FaUsers />
                  </div>
                  <span>Add Department User</span>
                </Link>
                <Link to="/IndividualAuditorCreate" className="action-card">
                  <div className="action-icon auditor-action">
                    <FaUserTie />
                  </div>
                  <span>Add Auditor</span>
                </Link>
                <Link to="/ChecklistOverview" className="action-card">
                  <div className="action-icon checklist-action">
                    <FaClipboardList />
                  </div>
                  <span>New Checklist</span>
                </Link>
                <Link to={`/IndividualUserProfile/${admin?.id}`} className="action-card">
                  <div className="action-icon settings-action">
                    <FaUserCog />
                  </div>
                  <span>Settings</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </Container>

      <style>
        {`
          .dashboard-container {
            background-color: #f8f9fa;
            min-height: 100vh;
          }
          
          /* Updated styles for non-header design */
          .admin-avatar-simple {
            width: 45px;
            height: 45px;
            background-color: rgba(var(--bs-primary-rgb), 0.1);
            border: 2px solid rgba(var(--bs-primary-rgb), 0.2);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
            color: var(--bs-primary);
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          
          .profile-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .greeting {
            font-weight: 600;
            font-size: 1.25rem;
          }
          
          .dashboard-badge {
            padding: 8px 16px;
            font-weight: 500;
            border-radius: 30px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          
          .dashboard-content {
            padding-bottom: 30px;
          }
          
          .dashboard-grid {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto;
            gap: 15px;
          }
          
          .dashboard-section {
            background-color: transparent;
            border-radius: 12px;
          }
          
          .section-title {
            font-weight: 600;
            color: #344767;
            display: flex;
            align-items: center;
            font-size: 1rem;
          }
          
          .stats-overview {
            margin-bottom: 5px;
          }
          
          .stats-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 15px;
          }
          
          .stat-card {
            border: none;
            border-radius: 12px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            overflow: hidden;
            height: 100%;
          }
          
          .stat-card .card-body {
            padding: 15px;
            display: flex;
            flex-direction: row;
            align-items: center;
            height: 100%;
          }
          
          .stat-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .stat-content {
            flex-grow: 1;
          }
          
          .stat-value {
            font-weight: 700;
            font-size: 1.5rem;
            margin-bottom: 2px;
            line-height: 1.2;
          }
          
          .stat-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6c757d;
            margin: 0;
          }
          
          .status-section {
            margin-bottom: 5px;
          }
          
          .status-tabs {
            border-bottom: 1px solid #e9ecef;
          }
          
          .status-tab {
            border: none;
            border-radius: 0;
            color: #6c757d;
            font-weight: 500;
            padding: 8px 16px;
            transition: all 0.2s ease;
          }
          
          .status-tab.active {
            color: var(--bs-primary);
            border-bottom: 2px solid var(--bs-primary);
            background: transparent;
          }
          
          .status-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
            padding: 15px 0;
            align-items: stretch;
          }
          
          .status-card {
            border: none;
            border-radius: 12px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          
          .status-card .card-body {
            padding: 15px;
            display: flex;
            flex-direction: row;
            align-items: center;
            height: 100%;
            flex: 1;
            min-height: 80px;
          }
          
          .status-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .status-content {
            flex-grow: 1;
          }
          
          .status-value {
            font-weight: 700;
            font-size: 1.5rem;
            margin-bottom: 2px;
            line-height: 1.2;
          }
          
          .status-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6c757d;
            margin: 0;
          }
          
          .progress-section {
            margin-bottom: 5px;
          }
          
          .accordion-button:not(.collapsed) {
            background-color: #f8f9fa;
            color: var(--bs-primary);
            box-shadow: none;
          }
          
          .accordion-button:focus {
            box-shadow: none;
            border-color: #e9ecef;
          }
          
          .actions-section {
            margin-bottom: 5px;
          }
          
          .quick-actions {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            gap: 15px;
          }
          
          .action-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 15px 10px;
            border-radius: 12px;
            background-color: white;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            text-decoration: none;
            color: #344767;
          }
          
          .action-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            margin-bottom: 8px;
          }
          
          .action-card span {
            font-weight: 500;
            font-size: 0.8rem;
          }
          
          .branch-icon, .branch-action {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .department-icon, .department-action {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .users-icon, .users-action {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .auditor-icon, .auditor-action {
            background-color: rgba(13, 202, 240, 0.1);
            color: #0dcaf0;
          }
          
          .checklist-icon, .checklist-action {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .settings-action {
            background-color: rgba(108, 117, 125, 0.1);
            color: #6c757d;
          }
          
          .pending-icon, .cap-pending-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-icon, .cap-progress-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .complete-icon, .cap-complete-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .stat-card:hover, .status-card:hover, .action-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          }
          
          @media (min-width: 992px) {
            .dashboard-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-template-areas:
                "stats progress"
                "status progress"
                "actions progress";
              gap: 20px;
              align-items: start;
            }
            
            .stats-overview {
              grid-area: stats;
            }
            
            .status-section {
              grid-area: status;
            }
            
            .progress-section {
              grid-area: progress;
              height: 100%;
              display: flex;
              flex-direction: column;
            }
            
            .actions-section {
              grid-area: actions;
            }
            
            .stats-cards {
              grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
              gap: 10px;
            }
            
            .status-cards {
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
              align-items: stretch;
            }
            
            .quick-actions {
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
            
            .stat-card .card-body {
              padding: 12px;
            }
            
            .status-card .card-body {
              padding: 12px;
              min-height: 70px;
            }
            
            .action-card {
              padding: 12px 8px;
            }
            
            .stat-value, .status-value {
              font-size: 1.25rem;
            }
            
            .stat-icon, .status-icon {
              width: 35px;
              height: 35px;
              font-size: 16px;
            }
            
            .action-icon {
              width: 35px;
              height: 35px;
              font-size: 16px;
            }
            
            .action-card span {
              font-size: 0.75rem;
            }
          }
          
          @media (min-width: 1200px) {
            .dashboard-grid {
              grid-template-columns: 2fr 3fr;
              gap: 25px;
            }
            
            .stats-cards {
              grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
              gap: 12px;
            }
            
            .status-cards {
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
              align-items: stretch;
            }
            
            .quick-actions {
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }
            
            .stat-card .card-body, .status-card .card-body {
              padding: 15px;
            }
            
            .status-card .card-body {
              min-height: 80px;
            }
            
            .action-card {
              padding: 15px 10px;
            }
            
            .stat-value, .status-value {
              font-size: 1.5rem;
            }
            
            .stat-icon, .status-icon, .action-icon {
              width: 40px;
              height: 40px;
              font-size: 18px;
            }
            
            .action-card span {
              font-size: 0.8rem;
            }
          }
          
          @media (min-width: 1400px) {
            .dashboard-grid {
              grid-template-columns: 1fr 2fr;
            }
          }
          
          @media (max-width: 991px) {
            .dashboard-grid {
              grid-template-columns: 1fr;
              grid-template-areas:
                "stats"
                "status"
                "progress"
                "actions";
            }
            
            .stats-overview {
              grid-area: stats;
            }
            
            .status-section {
              grid-area: status;
            }
            
            .progress-section {
              grid-area: progress;
            }
            
            .actions-section {
              grid-area: actions;
            }
          }
          
          @media (max-width: 576px) {
            .greeting {
              font-size: 1.1rem;
            }
            
            .quick-actions {
              grid-template-columns: repeat(2, 1fr);
            }
            
            .stat-value, .status-value {
              font-size: 1.25rem;
            }
          }
        `}
      </style>
    </Container>
  );
}

export default IndividualDashboard;